import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { Edit, Trash2, Plus } from "lucide-react";
import { PrinterDialog } from "./PrinterDialog";
import { toast } from "react-toastify";
import { usePrinterStore } from "../../../store/printerStore";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types";
import { Printer } from "../../../types/printer";

export const PrinterList = () => {
    const store = usePrinterStore();
    const { createItem, updateItem, deleteItem } = store;
    const { handleApiError } = useApiErrorHandler();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [printerToEdit, setPrinterToEdit] = useState<Printer | null>(null);

    const handleNew = () => {
        setPrinterToEdit(null);
        setIsModalOpen(true);
    };

    const handleEdit = (printer: Printer) => {
        setPrinterToEdit(printer);
        setIsModalOpen(true);
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (printer: Printer) => {
        const ok = await confirm(`${printer.name} yazıcısını silmek istiyor musun?`);
        if (ok) {
            try {
                await deleteItem(printer.id);
                toast.success("Yazıcı başarıyla silindi");
            } catch (error) {
                handleApiError(error);
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setPrinterToEdit(null);
        store.fetchDataDynamic();
    };

    const handleSavePrinter = async (data: any) => {
        try {
            if (printerToEdit) {
                await updateItem(printerToEdit.id, data);
                toast.success(`Yazıcı Güncellendi: ${data.name}`);
            } else {
                await createItem(data);
                toast.success(`Yeni Yazıcı Eklendi: ${data.name}`);
            }
            handleCloseModal();
        } catch (error) {
            handleApiError(error);
        }
    };

    // --- Template'ler (Tailwind sınıfları ile) ---
    const actionBodyTemplate = (printer: Printer) => (
        <TableActions
            actions={[
                {
                    icon: Edit,
                    colorClass: "text-blue-600 hover:bg-blue-50",
                    title: "Düzenle",
                    onClick: () => handleEdit(printer)
                },
                {
                    icon: Trash2,
                    colorClass: "text-red-500 hover:bg-red-50",
                    title: "Sil",
                    onClick: () => handleDeleteClick(printer)
                }
            ]}
        />
    );

    const printerColumns: DataTableColumn<Printer>[] = [
        { field: "name", header: "Yazıcı Adı", width: "250px", sortable: true },
        { field: "language", header: "Dil", width: "150px", sortable: true },
        { field: "dpi", header: "DPI", width: "100px", sortable: true },
        { field: "ipAddress", header: "IP Adresi", width: "150px" },
        { field: "actions", header: "İşlemler", width: "120px", body: actionBodyTemplate, align: "center" }
    ];

    return (
        <div className="w-full p-2">
            {confirmModal}
            
            {/* ApiDataTable zaten içeride bir card yapısına sahipse burası temiz kalacaktır */}
            <div className="bg-card rounded-xl border border-borderColor shadow-sm overflow-hidden h-full flex flex-col">
                <EDataTable<Printer>
                    tableKey="printer_list"
                    useStore={() => store}
                    dataKey="id"
                    title="Yazıcı Yönetimi"
                    placeholder="Yazıcı adı ara"
                    searchFields={[{ field: "name", operator: "contains" as SimpleFilter["operator"] }]}
                    actions={
                        <TextButton onClick={handleNew} className="bg-primary text-white hover:bg-primaryHover px-4 py-2 rounded-lg flex items-center gap-2 transition-all">
                            <Plus size={18} /> Yeni Yazıcı Ekle
                        </TextButton>
                    }
                    columns={printerColumns}
                />
            </div>

            <PrinterDialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                printer={printerToEdit}
                onSave={handleSavePrinter}
            />
        </div>
    );
};